/** アクセスポリシー(R7d)の問い合わせ。書き込みは idp:admin(真実は API 側)。 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import { useCallback } from "react";

import type { PolicyGroupForm } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { errorText, useIdpDownload } from "./hooks";

/** ポリシー関連のキャッシュをまとめて捨てる(状態の帯も引き直す)。 */
export function useInvalidatePolicy() {
  const qc = useQueryClient();
  return useCallback(() => {
    void qc.invalidateQueries({ queryKey: ["idp", "policy"] });
    // ユーザー一覧のポリシー列・判定ログ・監査も変わる
    void qc.invalidateQueries({ queryKey: ["idp", "users"] });
    void qc.invalidateQueries({ queryKey: ["idp", "user"] });
    void qc.invalidateQueries({ queryKey: ["idp", "logs"] });
    void qc.invalidateQueries({ queryKey: ["audit"] });
  }, [qc]);
}

/** 成功したら引き直して、失敗はそのまま出す(全ての書き込みで同じ形)。 */
function useWrite<TArgs, TOut>(fn: (args: TArgs) => Promise<TOut>, label: string) {
  const invalidate = useInvalidatePolicy();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      invalidate();
      void message.success(`${label}した`);
    },
    onError: (e) => void message.error(errorText(e)),
  });
}

export function usePolicyStatus(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ["idp", "policy", "status"],
    queryFn: api.policyStatus,
    enabled,
  });
}

/** 条件式に書ける原子と上限。変わらないので長めに持つ。 */
export function usePolicyAtoms(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ["idp", "policy", "atoms"],
    queryFn: api.policyAtoms,
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function usePolicySettings(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ["idp", "policy", "settings"],
    queryFn: api.policySettings,
    enabled,
  });
}

/**
 * `enforce` の切り替え。**本番の Keycloak のブラウザフローが変わる**ので、
 * 画面は確認ダイアログを通してからここに来る。
 */
export function useSavePolicySettings() {
  const api = useApi();
  const invalidate = useInvalidatePolicy();
  return useMutation({
    mutationFn: (enforce: boolean) => api.savePolicySettings({ enforce }),
    onSuccess: (out) => {
      invalidate();
      const kc = out.keycloak ?? {};
      const skipped = String(kc.skipped ?? "");
      // 有効化を Keycloak に入れられず、サーバーが false に戻した(3 巡目の高 1)
      const undone = String(kc.undone ?? "");
      // **戻せなかった**(9 巡目の低 10)。別の更新が入った / 実物は切り替わって
      // いる。安全な側にいると誤解させないよう、今の状態を見に行かせる
      const undoSkipped = String(kc.undoSkipped ?? "");
      if (undone) {
        void message.error(
          undone === "spi_missing"
            ? "Keycloak に SPI が入っていないので有効にできない(設定は無効に戻した)"
            : `Keycloak に反映できなかったので無効に戻した: ${undone}`,
        );
      } else if (undoSkipped) {
        void message.error(
          `無効に戻せなかった。状態を確認(${undoSkipped} / 今: enforce=${
            kc.enforce ?? out.data.enforce
          } applied=${String(kc.applied ?? "")})`,
        );
      } else if (skipped === "spi_missing") {
        void message.warning(
          "設定は保存したが、Keycloak に SPI が入っていないのでフローは切り替えていない",
        );
      } else if (skipped === "running") {
        // **失敗ではない**(fix2 の低 8)。別の反映が動いているだけなので戻さない
        void message.info(
          "設定は保存した(別の反映が動いているので反映待ち。終わり次第この設定で入る)",
        );
      } else if (out.data.enforce) {
        void message.success("アクセスポリシーを有効にした");
      } else {
        void message.success("アクセスポリシーを無効にした(ブラウザフローを戻した)");
      }
    },
    onError: (e) => void message.error(errorText(e)),
  });
}

export function useRecheckPolicyFlow() {
  const api = useApi();
  return useWrite(() => api.recheckPolicyFlow(), "Keycloak を確認");
}

/** 条件式の検証(入力欄の下に出す)。エラーでも例外にしない。 */
export function useCheckExpression() {
  const api = useApi();
  return useMutation({ mutationFn: (expr: string) => api.checkPolicyExpression(expr) });
}

export function usePolicyPreview() {
  const api = useApi();
  return useMutation({
    mutationFn: (body: {
      userId: string;
      username?: string;
      clientId?: string;
      ip?: string;
      cert?: boolean;
    }) => api.previewPolicy(body),
    onError: (e) => void message.error(errorText(e)),
  });
}

// ---- IP グループ -------------------------------------------------------------------

export function useIpGroups(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ["idp", "policy", "ip-groups"],
    queryFn: api.ipGroups,
    enabled,
  });
}

export function useCreateIpGroup() {
  const api = useApi();
  return useWrite(
    (data: { displayName: string; note?: string; entries: string }) =>
      api.createIpGroup(data),
    "IP グループを作成",
  );
}

export function useUpdateIpGroup() {
  const api = useApi();
  return useWrite(
    (args: { id: string; data: { displayName?: string; note?: string; entries?: string } }) =>
      api.updateIpGroup(args.id, args.data),
    "IP グループを保存",
  );
}

export function useDeleteIpGroup() {
  const api = useApi();
  return useWrite((id: string) => api.deleteIpGroup(id), "IP グループを削除");
}

export function useIpGroupsCsv() {
  const download = useIdpDownload();
  return useCallback(
    () => void download("/api/idp/policy/ip-groups/export.csv"),
    [download],
  );
}

// ---- テンプレート -------------------------------------------------------------------

export function usePolicyTemplates(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ["idp", "policy", "templates"],
    queryFn: api.policyTemplates,
    enabled,
  });
}

export function useCreatePolicyTemplate() {
  const api = useApi();
  return useWrite(
    (data: { key: string; displayName?: string; expression: string }) =>
      api.createPolicyTemplate(data),
    "テンプレートを作成",
  );
}

export function useUpdatePolicyTemplate() {
  const api = useApi();
  return useWrite(
    (args: {
      id: string;
      data: { key?: string; displayName?: string; expression?: string };
    }) => api.updatePolicyTemplate(args.id, args.data),
    "テンプレートを保存",
  );
}

export function useDeletePolicyTemplate() {
  const api = useApi();
  return useWrite((id: string) => api.deletePolicyTemplate(id), "テンプレートを削除");
}

// ---- ポリシーグループ ---------------------------------------------------------------

export function usePolicyGroups(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ["idp", "policy", "groups"],
    queryFn: api.policyGroups,
    enabled,
  });
}

export function usePolicyGroup(id: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ["idp", "policy", "group", id],
    queryFn: () => api.policyGroup(id!),
    enabled: !!id,
  });
}

/** 新規作成のときの SP 一覧(既存のポリシーは詳細の `sps` を使う)。 */
export function usePolicySps(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ["idp", "policy", "sps"],
    queryFn: api.policySps,
    enabled,
  });
}

export function useCreatePolicyGroup() {
  const api = useApi();
  return useWrite((data: PolicyGroupForm) => api.createPolicyGroup(data), "ポリシーを作成");
}

export function useUpdatePolicyGroup() {
  const api = useApi();
  return useWrite(
    (args: { id: string; data: PolicyGroupForm }) =>
      api.updatePolicyGroup(args.id, args.data),
    "ポリシーを保存",
  );
}

export function useDeletePolicyGroup() {
  const api = useApi();
  return useWrite((id: string) => api.deletePolicyGroup(id), "ポリシーを削除");
}

// ---- ユーザーへの割当 ---------------------------------------------------------------

export function useSetUserPolicy() {
  const api = useApi();
  return useWrite(
    (args: { id: string; groupId: string }) =>
      api.setUserPolicyGroup(args.id, args.groupId),
    "ポリシーを割り当て",
  );
}

export function useClearUserPolicy() {
  const api = useApi();
  return useWrite((id: string) => api.clearUserPolicyGroup(id), "割当を解除");
}

// ---- 判定ログ ----------------------------------------------------------------------

export function usePolicyDecisions(params: URLSearchParams) {
  const api = useApi();
  return useQuery({
    queryKey: ["idp", "logs", "policy", params.toString()],
    queryFn: () => api.idpLogPolicy(params),
  });
}
